import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { clearEquipmentTypeCatalogCache } from './equipmentTypeCatalogService';

interface AdminCreateEquipmentTypeInput {
  name: string;
  shortCode: string;
}

interface AdminCreateEquipmentTypeResponse {
  success: boolean;
  equipmentTypeId: string;
}

interface AdminUpdateEquipmentTypeInput {
  equipmentTypeId: string;
  name: string;
  shortCode: string;
}

interface AdminUpdateEquipmentTypeResponse {
  success: boolean;
  equipmentTypeId: string;
}

interface AdminSetEquipmentTypeActiveStateInput {
  equipmentTypeId: string;
  action: 'deactivate' | 'reactivate';
}

interface AdminSetEquipmentTypeActiveStateResponse {
  success: boolean;
  equipmentTypeId: string;
  status: 'active' | 'inactive';
}

interface ModerateEquipmentTypeSuggestionInput {
  suggestionId: string;
  action: 'approve' | 'reject';
  shortCode?: string;
  rejectionReason?: string;
}

interface ModerateEquipmentTypeSuggestionResponse {
  success: boolean;
  suggestionId: string;
  status: 'accepted' | 'rejected';
}

export const adminCreateEquipmentType = async (
  input: AdminCreateEquipmentTypeInput
): Promise<AdminCreateEquipmentTypeResponse> => {
  const fn = httpsCallable<AdminCreateEquipmentTypeInput, AdminCreateEquipmentTypeResponse>(
    functions,
    'adminCreateEquipmentType'
  );
  const result = await fn({
    name: String(input.name || '').trim(),
    shortCode: String(input.shortCode || '').trim(),
  });
  clearEquipmentTypeCatalogCache();
  return result.data;
};

export const adminUpdateEquipmentType = async (
  input: AdminUpdateEquipmentTypeInput
): Promise<AdminUpdateEquipmentTypeResponse> => {
  const fn = httpsCallable<AdminUpdateEquipmentTypeInput, AdminUpdateEquipmentTypeResponse>(
    functions,
    'adminUpdateEquipmentType'
  );
  const result = await fn({
    equipmentTypeId: String(input.equipmentTypeId || '').trim(),
    name: String(input.name || '').trim(),
    shortCode: String(input.shortCode || '').trim(),
  });
  clearEquipmentTypeCatalogCache();
  return result.data;
};

export const adminSetEquipmentTypeActiveState = async (
  input: AdminSetEquipmentTypeActiveStateInput
): Promise<AdminSetEquipmentTypeActiveStateResponse> => {
  const fn = httpsCallable<
    AdminSetEquipmentTypeActiveStateInput,
    AdminSetEquipmentTypeActiveStateResponse
  >(functions, 'adminSetEquipmentTypeActiveState');
  const result = await fn({
    equipmentTypeId: String(input.equipmentTypeId || '').trim(),
    action: input.action,
  });
  clearEquipmentTypeCatalogCache();
  return result.data;
};

export const moderateEquipmentTypeSuggestion = async (
  input: ModerateEquipmentTypeSuggestionInput
): Promise<ModerateEquipmentTypeSuggestionResponse> => {
  const fn = httpsCallable<
    ModerateEquipmentTypeSuggestionInput,
    ModerateEquipmentTypeSuggestionResponse
  >(functions, 'moderateEquipmentTypeSuggestion');
  const result = await fn({
    suggestionId: String(input.suggestionId || '').trim(),
    action: input.action,
    ...(typeof input.shortCode === 'string' ? { shortCode: input.shortCode.trim() } : {}),
    ...(typeof input.rejectionReason === 'string'
      ? { rejectionReason: input.rejectionReason.trim() }
      : {}),
  });
  if (result.data?.status === 'accepted') {
    clearEquipmentTypeCatalogCache();
  }
  return result.data;
};
